// Request util for api call
import request from '~/utils/request';
import { appendSortParams } from '~/utils/sort-params';

// Actions Types
import {
  SET_CONTACTS_DATA,
  SET_CONTACTS_LOADER,
  SET_CONTACTS_LABELS,
  SET_RETURN_ADDRESSES_DATA,
  SET_CONTACT_NOTES_DATA,
  SET_CONTACT_NOTES_LOADER,
  CREATE_CONTACT_NOTE,
  DELETE_CONTACT_NOTE,
  UPDATE_CONTACT_NOTE,
  UPDATE_SINGLE_CONTACT,
} from './action-types';

// Utiils
import { dateFormat } from '~/utils/date-format';
import { DEFAULT_PAGINATION } from '~/utils/constants';

const getFilterValue = (value) => {
  if (value && typeof value === 'object') {
    return value.title || value.value || value.id;
  }

  return value;
};

const fetchContacts = (payload) => async (dispatch) => {
  try {
    dispatch({ type: SET_CONTACTS_LOADER, payload: true });

    let url = '/contacts';

    let queryPayload = {
      page: payload?.pagination?.page ? payload.pagination.page : DEFAULT_PAGINATION.page,
      pageSize: payload?.pagination?.pageSize
        ? payload.pagination.pageSize
        : DEFAULT_PAGINATION.pageSize,
    };

    if (typeof payload?.isReturnAddress === 'boolean') {
      queryPayload.isReturnAddress = payload.isReturnAddress;
    }

    if (typeof payload?.excludeDncList === 'boolean') {
      queryPayload.excludeDncList = payload.excludeDncList;
    }

    if (payload?.search?.length) {
      queryPayload.search = payload.search;
    }

    appendSortParams(queryPayload, payload);

    if (payload?.applyFilters) {
      Object.entries(payload.filters).forEach(([key, value]) => {
        if (key === 'lastMailedDate' || key === 'createdAt' || key === 'lastQrScannedDate') {
          // If the value is an object, iterate over its properties
          if (value) {
            let date = Object?.entries(value)
              .map(
                ([nestedKey, nestedValue]) =>
                  `${JSON.stringify(nestedKey)}:${JSON.stringify(nestedValue)}`
              )
              .join(', ');
            queryPayload[key] = `{${date}}`;
          }
        } else if (key === 'tags' && value.length) {
          queryPayload[key] = `[${value}]`;
        } else if (Array.isArray(value) && value.length) {
          queryPayload[key] = `${JSON.stringify(value.map(getFilterValue))}`;
        } else if (typeof value === 'boolean' && value === true) {
          queryPayload[key] = 'true';
        } else if (typeof value === 'string' && value.trim()) {
          queryPayload[key] = `${JSON.stringify(value)}`;
        }
      });
    }

    const response = await request.get(url, queryPayload);

    if (response.status === 200) {
      const combinedData = response.data.data.rows.map((contact) => ({
        ...contact,
        fullName: contact.fullName,
        createdAt: dateFormat(contact.createdAt),
        lastMailedStatus: contact.lastMailedStatus ? contact.lastMailedStatus : ' -',
        tags: contact.ContactLabels.map((label) => label.title),
        checked: false,
      }));
      const contacts = {
        rows: combinedData,
        count: response.data.data.count,
        totalRecordsInDB: response.data.data.totalRecordsInDB,
        to: response.data.data.to,
        from: response.data.data.from,
        currentPage: response.data.data.currentPage,
        lastPage: response.data.data.lastPage,
        perPage: response.data.data.perPage,
      };
      dispatch({ type: SET_CONTACTS_DATA, payload: contacts });
    }

    return response;
  } catch (error) {
    return error;
  } finally {
    dispatch({ type: SET_CONTACTS_LOADER, payload: false });
  }
};

const fetchReturnAddresses = (payload) => async (dispatch) => {
  try {
    const returnAddresses = await request.get('contacts', payload);
    dispatch({ type: SET_RETURN_ADDRESSES_DATA, payload: returnAddresses.data.data.rows });
  } catch (error) {
    return error;
  }
};

const fetchContactLabels = () => async (dispatch) => {
  try {
    const labels = await request.get('contacts/labels', { pageSize: 500 });
    dispatch({ type: SET_CONTACTS_LABELS, payload: labels.data.data.rows });
  } catch (error) {
    return error;
  }
};

// New helper for advanced tag fetching with pagination/search (used by new AddContacts flow)
const fetchContactLabelsWithSearch =
  (payload = {}) =>
  async (dispatch) => {
    try {
      const queryPayload = {
        pageSize: payload?.pageSize || 500,
        page: payload?.page || DEFAULT_PAGINATION.page,
      };

      if (payload?.search && payload.search.trim()) {
        queryPayload.search = payload.search.trim();
      }

      const labels = await request.get('contacts/labels', queryPayload);
      dispatch({ type: SET_CONTACTS_LABELS, payload: labels.data.data.rows });
      return labels;
    } catch (error) {
      return error;
    }
  };

const fetchContactCount = (payload) => async () => {
  try {
    const count = await request.post('contacts/labels/getContactsCount', payload);
    return count;
  } catch (error) {
    return error.response;
  }
};

const fetchExpectedDeliveryDate = (payload) => async () => {
  try {
    const dated = await request.post('orders/get-expected-mailed-date', payload);
    return dated;
  } catch (error) {
    return error.response;
  }
};

const createContact = (payload) => async () => {
  try {
    const contact = await request.post('contacts', payload);
    return contact;
  } catch (error) {
    return error.response;
  }
};

const updateContact = (payload) => async () => {
  try {
    const contact = await request.put(`contacts/${payload.id}`, payload);
    return contact;
  } catch (error) {
    return error.response;
  }
};

const updateSenderProfile = (payload) => async (dispatch) => {
  try {
    const contact = await request.put(`contacts/${payload.id}`, payload);

    // Dispatch action to update the contact in Redux state
    if (contact.status === 200 && contact.data?.data) {
      dispatch({ type: UPDATE_SINGLE_CONTACT, payload: contact.data.data });
    }

    return contact;
  } catch (error) {
    return error.response;
  }
};

const updateContactCustomFields = (contactId, payload) => async () => {
  try {
    const customFields = await request.post(`contacts/custom-fields/${contactId}`, payload);
    return customFields;
  } catch (error) {
    return error.response;
  }
};

const contactBulkActions = async (payload) => {
  try {
    const queryPayload = {};

    queryPayload.ids = payload.ids;
    queryPayload.action = payload.action;

    if (payload?.search?.length || payload?.applyFilters) {
      queryPayload.filters = {};
    }
    if (payload?.search?.length) {
      queryPayload.filters.search = payload.search;
    }

    if (payload?.applyFilters) {
      Object.entries(payload.filters).forEach(([key, value]) => {
        if (key === 'lastMailedDate' || key === 'createdAt' || key === 'lastQrScannedDate') {
          // If the value is an object, iterate over its properties
          if (value) {
            queryPayload.filters[key] = value;
          }
        } else if (Array.isArray(value) && value.length) {
          queryPayload.filters[key] = value.map(getFilterValue);
        } else if (typeof value === 'boolean' && value === true) {
          queryPayload.filters[key] = value;
        } else if (
          value !== null &&
          value !== undefined &&
          !Array.isArray(value) &&
          typeof value !== 'boolean' &&
          String(value).trim()
        ) {
          queryPayload.filters[key] = value;
        }
      });
    }

    const contactBulk = await request.post('contacts/bulk-actions', queryPayload);
    return contactBulk;
  } catch (error) {
    return error.response;
  }
};

const updateContactTags = (payload) => async () => {
  try {
    const queryPayload = {};

    queryPayload.contactIds = payload.contactIds;
    queryPayload.tagIds = payload.tagIds;
    queryPayload.action = payload.action;

    if (payload?.search?.length || payload?.applyFilters) {
      queryPayload.filters = {};
    }
    if (payload?.search?.length) {
      queryPayload.filters.search = payload.search;
    }

    if (payload?.applyFilters) {
      Object.entries(payload.filters).forEach(([key, value]) => {
        if (key === 'lastMailedDate' || key === 'createdAt' || key === 'lastQrScannedDate') {
          // If the value is an object, iterate over its properties
          if (value) {
            queryPayload.filters[key] = value;
          }
        } else if (Array.isArray(value) && value.length) {
          queryPayload.filters[key] = value.map(getFilterValue);
        } else if (typeof value === 'boolean' && value === true) {
          queryPayload.filters[key] = value;
        } else if (
          value !== null &&
          value !== undefined &&
          !Array.isArray(value) &&
          typeof value !== 'boolean' &&
          String(value).trim()
        ) {
          queryPayload.filters[key] = value;
        }
      });
    }
    const updateTags = await request.post(
      `contacts/labels/update-contact-association`,
      queryPayload
    );
    return updateTags;
  } catch (error) {
    return error.response;
  }
};

const fetchContactNotes = (contactId) => async (dispatch) => {
  dispatch({ type: SET_CONTACT_NOTES_LOADER, payload: true });
  try {
    const notes = await request.get(`contact-notes/contact/${contactId}`);
    dispatch({ type: SET_CONTACT_NOTES_DATA, payload: notes.data.data.rows });
    dispatch({ type: SET_CONTACT_NOTES_LOADER, payload: false });
    return notes;
  } catch (error) {
    return error.response;
  }
};

const createContactNote = (payload) => async (dispatch) => {
  try {
    const note = await request.post('contact-notes', payload);
    dispatch({ type: CREATE_CONTACT_NOTE, payload: note.data.data });
    return note;
  } catch (error) {
    return error.response;
  }
};

const updateContactNote = (payload) => async (dispatch) => {
  try {
    const note = await request.put(`contact-notes/${payload.id}`, payload);
    dispatch({ type: UPDATE_CONTACT_NOTE, payload: note.data.data });
    return note;
  } catch (error) {
    return error.response;
  }
};

const deleteContactNote = (payload) => async (dispatch) => {
  try {
    const note = await request.delete(`contact-notes/${payload.id}`);
    dispatch({ type: DELETE_CONTACT_NOTE, payload: { id: payload.id } });
    return note;
  } catch (error) {
    return error.response;
  }
};

const markDNC = (payload) => async () => {
  try {
    const dnc = await request.patch(`contacts/${payload.contactId}`, payload);
    return dnc;
  } catch (error) {
    return error.response;
  }
};

const fetchDeliverableContacts = (payload) => async () => {
  try {
    const body = { ids: payload.ids, filters: {} };

    if (payload.applyFilters) {
      Object.entries(payload.filters).forEach(([key, value]) => {
        if (key === 'tags' && Array.isArray(value) && value.length) {
          body.filters[key] = value.map(Number);
        } else if (Array.isArray(value) && value.length) {
          body.filters[key] = value.map(getFilterValue);
        } else if (typeof value === 'boolean' && value === true) {
          body.filters[key] = value;
        } else if (value !== null && !Array.isArray(value) && typeof value !== 'boolean') {
          body.filters[key] = value;
        }
      });
    }

    const response = await request.post('contacts/get-deliverable-contacts', body);
    return response;
  } catch (error) {
    return error.response;
  }
};

// Fetch specific contacts by IDs without updating the Redux contacts store.
// Used to hydrate the selectedContactsCache with full contact objects when
// pre-selected contacts may not be in the initial 20 shown in the dropdown.
const fetchContactsByIds = (ids) => async () => {
  try {
    const response = await request.get('/contacts', {
      page: 1,
      pageSize: ids.length,
      ids: JSON.stringify(ids),
      isReturnAddress: false,
      excludeDncList: true,
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

// Fetch contacts for the template builder preview picker without touching the
// shared Redux contacts store (so the Contacts/Orders lists are not overwritten).
const fetchContactsForTemplatePreview =
  ({ page = 1, pageSize = 25, search } = {}) =>
  async () => {
    try {
      const response = await request.get('/contacts', {
        page,
        pageSize,
        ...(search ? { search } : {}),
        isReturnAddress: false,
        excludeDncList: true,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

export {
  fetchContacts,
  fetchContactsForTemplatePreview,
  fetchContactLabels,
  fetchContactLabelsWithSearch,
  createContact,
  fetchContactCount,
  fetchExpectedDeliveryDate,
  fetchDeliverableContacts,
  fetchContactsByIds,
  updateContact,
  updateSenderProfile,
  updateContactCustomFields,
  contactBulkActions,
  fetchReturnAddresses,
  updateContactTags,
  fetchContactNotes,
  createContactNote,
  updateContactNote,
  deleteContactNote,
  markDNC,
};
